import type { RefObject } from 'react'
import { useLayoutEffect, useRef } from 'react'

/** Mirrors --dur-med / --ease-out in styles/tokens.css. */
const DURATION_MS = 240
const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'

type Options = {
  /** Attribute carrying each child's stable identity. */
  keyAttribute?: string
}

function measure(
  container: HTMLElement,
  keyAttribute: string,
): Map<string, number> {
  const tops = new Map<string, number>()
  for (const child of container.querySelectorAll<HTMLElement>(
    `[${keyAttribute}]`,
  )) {
    const key = child.getAttribute(keyAttribute)
    // offsetTop, not getBoundingClientRect: it's relative to the offset
    // parent rather than the viewport, so scrolling between renders can't
    // corrupt the delta.
    if (key) tops.set(key, child.offsetTop)
  }
  return tops
}

/**
 * FLIP-animates a re-sorted list: after the DOM settles in its new order,
 * each moved child is offset back to where it was and animated to zero, so a
 * row that jumps three places visibly slides there instead of teleporting.
 *
 * `order` should change identity only when the order does — pass the joined
 * keys, not the array.
 */
export function useFlipReorder(
  containerRef: RefObject<HTMLElement | null>,
  order: string,
  { keyAttribute = 'data-flip-key' }: Options = {},
) {
  const previous = useRef<Map<string, number> | null>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const current = measure(container, keyAttribute)
    const before = previous.current
    previous.current = current

    if (!before) return

    // Honour the same reduced-motion contract as .willfade. Feature-detect
    // animate() as well: jsdom implements no Web Animations API.
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof container.animate !== 'function'
    ) {
      return
    }

    for (const child of container.querySelectorAll<HTMLElement>(
      `[${keyAttribute}]`,
    )) {
      const key = child.getAttribute(keyAttribute)
      if (!key) continue

      const from = before.get(key)
      const to = current.get(key)
      if (from === undefined || to === undefined) continue

      const delta = from - to
      if (delta === 0) continue

      child.animate(
        [{ transform: `translateY(${delta}px)` }, { transform: 'none' }],
        { duration: DURATION_MS, easing: EASING },
      )
    }
  }, [containerRef, order, keyAttribute])
}
